/**
 * Icono por nombre: extiende el modelo de `ion-icon` — si `name` está en el catálogo cerrado
 * se pinta SVG desde `assets/upp-wdgt/icon/<name>.svg` (`currentColor`); si `name` representa
 * clases Font Awesome (`fa-*`) se renderiza `<i class="...">`; en caso contrario se renderiza
 * `ion-icon` con el mismo `name`.
 */
import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { Subscription } from 'rxjs';

// --- Catálogo `name` (análogo a `ion-icon`) ---------------------------------

/** Nombres válidos para {@link UppIconComponent} (`name`). */
export const UPP_ICON_NAMES = [
    'item-bar',
    'item-billiard',
    'item-door',
    'item-football',
    'item-generic',
    'item-sofa',
    'item-stairs',
    'item-table',
    'item-window',
] as const;

export type UppIconName = (typeof UPP_ICON_NAMES)[number];

const _uppIconNameSet: ReadonlySet<string> = new Set(UPP_ICON_NAMES);

/** Comprueba si la cadena es un nombre de icono registrado (lista cerrada). */
export function isUppIconName(name: string): name is UppIconName {
    return _uppIconNameSet.has(name);
}

const _faStyleTokens: ReadonlySet<string> = new Set([
    'fa',
    'fas',
    'far',
    'fab',
    'fal',
    'fat',
    'fad',
    'fa-solid',
    'fa-regular',
    'fa-brands',
    'fa-light',
    'fa-thin',
    'fa-duotone',
    'fa-sharp',
]);

function _isFaClassToken(raw: string): boolean {
    return /^fa-[a-z0-9-]+$/i.test(raw) || _faStyleTokens.has(raw);
}

/** Comprueba si `name` contiene una clase Font Awesome válida para render por `<i class="...">`. */
export function isFontAwesomeClassName(name: string): boolean {
    const tokens = name
        .split(/\s+/)
        .map((x) => x.trim())
        .filter((x) => x.length > 0);
    if (tokens.length === 0) {
        return false;
    }
    const hasFaPrefix = tokens.some((x) => x.startsWith('fa'));
    const hasStyleToken = tokens.some((x) => _faStyleTokens.has(x));
    const hasIconToken = tokens.some((x) => x.startsWith('fa-') && !_faStyleTokens.has(x));
    return hasFaPrefix && hasStyleToken && hasIconToken && tokens.every(_isFaClassToken);
}

/** Prefijo de URL bajo `angular.json` assets (ver apps que copian `libs/upp-wdgt/assets`). */
export const UPP_ICON_ASSET_BASE = 'assets/upp-wdgt/icon';

// --- Caché de SVG cargados --------------------------------------------------

const _svgTextCache = new Map<string, string>();

/** Vacía la caché en memoria (uso en unit tests). */
export function clearUppIconSvgCacheForTesting(): void {
    _svgTextCache.clear();
}

@Component({
    selector: 'upp-icon',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-icon.html',
    styleUrls: ['./upp-icon.scss'],
})
export class UppIconComponent implements OnChanges, OnDestroy {
    /** Nombre del icono: catálogo `UPP_ICON_NAMES` → SVG; otro valor → `ion-icon`. */
    @Input() name = '';

    /** Tamaño del cuadro del icono (p. ej. `40px`, `1.25rem`). Por defecto `1em`. */
    @Input() size?: string;

    /** Color temático Ionic (`primary`, `danger`, …); SVG usa `currentColor` vía `var(--ion-color-<color>)`; en rama `ion-icon` se pasa al hijo. */
    @Input() color: string | null = null;

    @HostBinding('attr.aria-hidden')
    readonly hostAriaHidden = true;

    @HostBinding('style.--upp-icon-size')
    get hostIconSize(): string | null {
        const s = this.size?.trim();
        return s ? s : null;
    }

    /** En ramas SVG/FA: tinte explícito; en rama `ion-icon` lo aplica el hijo vía `[color]`. */
    @HostBinding('style.color')
    get hostThemedColor(): string | null {
        const c = this.colorForIon;
        if (!c || (!this.rendersUppSvg && !this.rendersFaIcon)) {
            return null;
        }
        return `var(--ion-color-${c})`;
    }

    /** Nombre recortado; vacío → no se pinta nada. */
    get trimmedName(): string {
        return (this.name || '').trim();
    }

    /** Si es true, la vista usa SVG propio; si es false pero `trimmedName` no vacío, usa `ion-icon`. */
    get rendersUppSvg(): boolean {
        return this.trimmedName.length > 0 && isUppIconName(this.trimmedName);
    }

    /** Si es true, la vista usa `<i class="...">` con clases Font Awesome. */
    get rendersFaIcon(): boolean {
        return this.trimmedName.length > 0 && !this.rendersUppSvg && isFontAwesomeClassName(this.trimmedName);
    }

    /** Clases FA normalizadas para bind de clase en plantilla. */
    get fontAwesomeClassName(): string {
        return this.trimmedName;
    }

    /** Nombre de paleta Ionic para `[color]` del hijo (o `undefined` si no aplica). */
    get colorForIon(): string | undefined {
        const c = (this.color ?? '').trim();
        return c ? c : undefined;
    }

    svgHtml: SafeHtml | null = null;

    private _loadSub: Subscription | null = null;

    constructor(
        private readonly _http: HttpClient,
        private readonly _sanitizer: DomSanitizer,
        private readonly _cdr: ChangeDetectorRef,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['name']) {
            this._reload();
        }
        if (changes['color'] || changes['size']) {
            this._cdr.markForCheck();
        }
    }

    ngOnDestroy(): void {
        this._loadSub?.unsubscribe();
        this._loadSub = null;
    }

    private _reload(): void {
        this._loadSub?.unsubscribe();
        this._loadSub = null;

        const rawName = (this.name || '').trim();
        if (!isUppIconName(rawName)) {
            this.svgHtml = null;
            this._cdr.markForCheck();
            return;
        }

        if (_svgTextCache.has(rawName)) {
            const cachedSvg = _svgTextCache.get(rawName);
            if (cachedSvg !== undefined) {
                this.svgHtml = this._sanitizer.bypassSecurityTrustHtml(cachedSvg);
                this._cdr.markForCheck();
                return;
            }
        }

        const url = `${UPP_ICON_ASSET_BASE}/${rawName}.svg`;
        this._loadSub = this._http.get(url, { responseType: 'text' }).subscribe({
            next: (text) => {
                const body = text.replace(/<\?xml[^?]*\?>\s*/i, '').trim();
                _svgTextCache.set(rawName, body);
                this.svgHtml = this._sanitizer.bypassSecurityTrustHtml(body);
                this._cdr.markForCheck();
            },
            error: () => {
                this.svgHtml = null;
                this._cdr.markForCheck();
            },
        });
    }
}
